import { defineNuxtPlugin } from '#app';
import { useAuthClient } from '#composables/useAuthClient';
import { useConsentState } from '#bubba/shared/composables/consent/useConsentState';
import { createLogger } from '#bubba/shared/utils/logger';
import { getCachedSession } from '#bubba/shared/utils/sessionCache';

const log = createLogger('ConsentMerge', { enabled: false });

const MERGE_FLAG_KEY = 'bubba_consent_merge_done';

/**
 * Post-authentication anonymous consent merge plugin (website).
 *
 * After a user logs in (via email, passkey, or OAuth), this plugin checks
 * if there are anonymous consent records from before login and merges them
 * into the user's authenticated consent records.
 *
 * Runs once per fingerprintId per device. Defers execution (2s) to ensure
 * the Convex client has had time to authenticate.
 */
export default defineNuxtPlugin(() => {
    if (typeof window === 'undefined') return;

    const { state } = useConsentState();
    const { fingerprintId } = state.value;

    // No fingerprint → no anonymous consent records to merge
    if (!fingerprintId) return;

    // Already merged for this fingerprint → skip
    const mergedFlag = localStorage.getItem(MERGE_FLAG_KEY);
    if (mergedFlag === fingerprintId) return;

    const authClient = useAuthClient();

    // Defer the merge to avoid blocking app startup.
    // Wait a tick to ensure Convex auth handshake has completed.
    setTimeout(async () => {
      try {
        // Only merge when the user is actually authenticated — calling the
        // mutation unauthenticated spams the Convex logs every auth cycle.
        const session = await getCachedSession(authClient);
        if (!session?.data) {
          log.debug('Not authenticated, skipping consent merge until next login');
          return;
        }

        const { useConsentSync } = await import('#composables/useConsentSync');
        const { mergeAnonymousOnLogin, pullFromServer } = useConsentSync();

        const merged = await mergeAnonymousOnLogin();
        if (merged > 0) {
          // Also pull server state to update local cookie preferences
          await pullFromServer();
          log.info('Merged anonymous consents on login', { fingerprintId, merged });
        }

        // Mark as merged for this fingerprint so we don't retry
        localStorage.setItem(MERGE_FLAG_KEY, fingerprintId);
      } catch (e: unknown) {
        // Merging is best-effort — don't block the app.
        // If the user is not authenticated yet (fresh page load without session),
        // the mutation will fail silently and we'll retry on next page load.
        log.debug('Consent merge skipped or failed (user may not be authenticated yet)', e);
      }
    }, 2000);
});
